import { DOMAIN, PROTOCOL, GLOBAL_APP_DOMAIN, LITE_APP_DOMAIN, AU_APP_DOMAIN, EU_APP_DOMAIN } from '../config';

const CREDENTIALS = 'include';

export const SERVICES = {
  USERS_SERVICE: `${PROTOCOL}users.${DOMAIN}/v1/`,
  AUTH_SERVICE: `${PROTOCOL}auth.${DOMAIN}/v1/`,
  HUBSPOT_SERVICE: `${PROTOCOL}hubspot-sync.${DOMAIN}/v1/`,
  EXPORT_IMPORT_SERVICE_GLOBAL: `${PROTOCOL}responses-export-import${GLOBAL_APP_DOMAIN}/api/`,
  EXPORT_IMPORT_SERVICE_LITE: `${PROTOCOL}responses-export-import${LITE_APP_DOMAIN}/api/`,
  // use for Features.PlgExternal3
  USERS_SERVICE_V2: `${PROTOCOL}users.${DOMAIN}/v2/`,
  PROJECTS_SERVICE_V2_GLOBAL:  `${PROTOCOL}sx-projects${GLOBAL_APP_DOMAIN}/projects-api/v2`,
  PROJECTS_SERVICE_V2_AU:  `${PROTOCOL}sx-projects${AU_APP_DOMAIN}/projects-api/v2`,
  PROJECTS_SERVICE_V2_EUROPE:  `${PROTOCOL}sx-projects${EU_APP_DOMAIN}/projects-api/v2`,
  CAMPAIGNS_SERVICE: `${PROTOCOL}campaigns${GLOBAL_APP_DOMAIN}/campaigns-service/v1`,
  CAMPAIGNS_SERVICE_AU:  `${PROTOCOL}campaigns${AU_APP_DOMAIN}/campaigns-service/v1`,
  CAMPAIGNS_SERVICE_EUROPE:  `${PROTOCOL}campaigns${EU_APP_DOMAIN}/campaigns-service/v1`
  // end of use for Features.PlgExternal3
};
export const CORE_SERVICES = {
  EVENTS_COLLECTOR_SERVICE:  `${PROTOCOL}events-collector.${DOMAIN}`, // we get cors error if we put it in SERVICES above, something to do with "credentials" header
  EXPORT_IMPORT_SERVICE: `${PROTOCOL}responses-export-import`,
  PROJECTS_SERVICE_V2:  `${PROTOCOL}sx-projects`,
  CAMPAIGNS_SERVICE_V2: `${PROTOCOL}campaigns`
};

export enum HttpMethods {
  Get = 'GET',
  Post = 'POST',
  Put = 'PUT',
  Delete = 'DELETE',
  Patch = 'PATCH'
}

export enum CustomHeaders {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  X_AUTH_USER = 'X-Auth-User',
}

export enum ContentTypes {
  ApplicationJson = 'application/json',
  ApplicationXml = 'application/xml',
}

export enum StatusTypes {
  UpgradeInProgress = 'upgrade_in_progress',
  ExpiredAccount = 'expired_account',
  UserInactive = 'user_inactive',
  SsoOnlyEnabled = 'sso_only',
  RecaptchaError = 'recaptcha_error'
}

export enum PayloadTypes {
  TwoFactorRequired = '2fa_required',
  LoginSuccessful = 'login_successful'
}

interface IErrorResponse {
  statusCode: number;
  message: string;
  status: string;
  errors?: Record<string, string[]>;
  // use for Features.plgexternal3
  url?: string;
  userError?: boolean;
  // end of use for Features.plgexternal3
}

export class HttpError {
  public statusCode: number;
  public message: string;
  public status: string;
  public errors?: Record<string, string[]>;
  // use for Features.plgexternal3
  public url?: string;
  public userError?: boolean;
  // end of use for Features.plgexternal3

  constructor(response: IErrorResponse) {
    this.statusCode = response.statusCode;
    this.message = response.message;
    this.status = response.status;
    this.errors = response.errors;
    this.url = response.url;
    this.userError = response.userError;
  }
}

export interface ApiRequest<T> {
  service: string;
  url: string;
  config?: RequestInit;
  body?: T;
}

export interface ApiError {
  statusCode?: number;
  message?: string;
  status?: string;
  url?: string;
  userError?: boolean;
}

export const DEFAULT_CONFIG: RequestInit = {
  credentials: CREDENTIALS,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'Accept-Encoding': 'gzip'
  }
};

class API {
  static async http<T>(service: string, url: string, config: RequestInit): Promise<T> {
    const request = new Request(`${service}${url}`, {
      credentials: CREDENTIALS,
      ...config
    });
    const response = await fetch(request);
    if (!response.ok) {
      const parsedResponse = await response.json().catch(() => ({}));
      const message = parsedResponse.message ? parsedResponse.message : response.statusText;
      return Promise.reject(new HttpError({
        statusCode: response.status,
        message,
        status: parsedResponse.status,
        errors: parsedResponse.errors,
        url: parsedResponse.url,
        userError: parsedResponse.userError
      }));
    }
    if (response.status === 204 && response.headers && response.headers.has(CustomHeaders.X_AUTH_USER)) {
      return JSON.parse(response.headers.get(CustomHeaders.X_AUTH_USER) || '');
    }

    return response.json().catch(() => ({}));
  }

  static get<T, U>(request: ApiRequest<U>): Promise<T> {
    const config = request.config || DEFAULT_CONFIG;
    const init = { ...config, method: 'get' };
    return API.http<T>(request.service, request.url, init);
  }

  static post<T, U>(request: ApiRequest<T>): Promise<U> {
    const config = request.config || DEFAULT_CONFIG;
    const body = request.body || {};
    const init = { ...config, method: 'post', body: JSON.stringify(body) };
    return API.http<U>(request.service, request.url, init);
  }

  static put<T, U>(request: ApiRequest<T>): Promise<U> {
    const config = request.config || DEFAULT_CONFIG;
    const body = request.body || {};
    const init = { ...config, method: 'put', body: JSON.stringify(body) };
    return API.http<U>(request.service, request.url, init);
  }

  static patch<T, U>(request: ApiRequest<T>): Promise<U> {
    const config = request.config || DEFAULT_CONFIG;
    const body = request.body || {};
    const init = { ...config, method: 'patch', body: JSON.stringify(body) };
    return API.http<U>(request.service, request.url, init);
  }

  static delete<T, U>(request: ApiRequest<T>): Promise<U> {
    const config = request.config || DEFAULT_CONFIG;
    const body = request.body || {};
    const init = { ...config, method: 'delete', body: JSON.stringify(body) };
    return API.http<U>(request.service, request.url, init);
  }
}

export default API;
